/** Component for an attachment activity */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { XsI18nPipe, FileSizeFormatPipe } from '@nx-fe/pipes';
import {
  ErrorHandlerService,
  FeatureToggleService,
  GlobalEventBusService,
  hd,
  IEmailRecipient,
  LicenceInfoService,
  LicenceStatus,
  LoggerService,
  TicketContextService,
  TicketService,
  UserInfoService,
} from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { AttachmentService, IAttachmentModel } from 'app/modules/ticket/services/attachment.service';
import { Observable, zip } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';
import { ActivityContextService } from 'app/modules/ticket/components/ticket-details-panel/activity/activity-context';
import {
  AttachmentActivity,
  UploadCompleteReason,
  Activity,
  ActivityTypeEnum,
  NoteActivity,
} from '../../../ticket.model';
import { EmailActivityService } from 'app/modules/ticket/services/email.service';
import { WatcherService } from 'app/services/watcher.service';
import { DatePipe } from '@angular/common';

//type IUploadPromise = hd.IUploadPromise;
type IUploadResult = hd.IUploadResult;
type IActivityModel = hd.IActivityModel;
type IAttachmentActivity = hd.IAttachmentActivity;

/*
type IFilterService = hd.IFilterService;
*/

@Component({
  selector: 'xs-attachment-activity',
  templateUrl: './attachment-activity.component.html',
  styleUrls: ['./attachment-activity.component.scss'],
})
export class AttachmentActivityComponent implements OnInit {
  /** The activity for this attachment, set during controller initialization */
  @Input() activity: AttachmentActivity;

  /** Indicates whether the note activity is editable or not */
  @Input()
  public canEdit: boolean;

  /** Indicates that we are calling that component in Self Service Portal */
  @Input()
  public isSPPMode: boolean;

  /** The ActivityController exposes methods for saving, deleting, and updating activities */
  @Input() activityCtrl: ActivityContextService;

  /** Selected file for uploading/attaching - not set if for an existing attachment */
  @Input() file: File;

  /** Current upload progress (if applicable) */
  public progress = 0;

  /** Error encountered during uploading or saving of attachment activity */
  @Input() error: string;

  @Output()
  addedAttachment: EventEmitter<any> = new EventEmitter();

  @Output()
  isEditting = new EventEmitter<boolean>();

  /** The child attachment note activity */
  public noteActivity: NoteActivity;

  // Whether or not a file upload cancellation was requested
  public cancelRequested = false;

  /** Boolean indicating whether a failed operation can be retried */
  @Input()
  public canRetry: boolean;

  // Whether dismissing of errors is enabled
  public enableDismissError = true;

  public licenceInfo: hd.ILicenceDetails;

  private _islicenceExpired = true;

  // Promise returned from file upload operation
  private uploadPromise: Observable<IAttachmentModel>;

  private _isCheckingForUpload = false;

  private watcherList;
  private userInfoList;

  //private attachment: AttachmentActivity;

  // Time (in ms) to delay before hiding the progress indicator after an upload completes
  private uploadCompleteDelay = 1000;
  private editing = false;

  constructor(
    private attachmentService: AttachmentService,
    private $filter: XsI18nPipe,
    private ticketService: TicketService,
    private errorHandlerService: ErrorHandlerService,
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    private licenceInfoService: LicenceInfoService,
    private $log: LoggerService,
    private globalEventBusService: GlobalEventBusService,
    private fileSizeFormatter: FileSizeFormatPipe,
    private emailService: EmailActivityService,
    private watcherService: WatcherService,
    private userInfoService: UserInfoService,
    private datePipe: DatePipe
  ) {}

  /**
   * Called to pass required information during controller initialization.
   * @param activity A new or existing attachment activity.
   * @param file Optional file selected to upload (for new attachments).
   */
  ngOnInit() {
    // Store the passed in file object on the controller and clear it from the activity
    this.file = this.activity.$$file;
    this.activity.$$file = undefined;
    this.licenceInfoService.getLicenceInformation().subscribe(x => {
      this.licenceInfo = x as any;
    });

    // Set initial activity state
    this.updateActivityState();

    if (this.isNew()) {
      // New attachment activity
      if (this.file) {
        // Set filename for attachment activity
        this.activity.fileName = this.file.name;
        this.activity.fileSize = this.file.size;

        if (this.isLocal()) {
          // this.enableDismissError = false;
          // this._isCheckingForUpload = true;

          // Start the file upload
          this.uploadFile();
        }
      }
    } else {
      if (this.activity.displayName) {
        this.setupCustomIconCallback();
      }
    }

    // this.prepareNoteActivity();
    if (this.licenceInfo.status === LicenceStatus.active) {
      this._islicenceExpired = false;
    }
  }

  /** Retries a failed attachment operation */
  public retry = (): void => {
    // File upload failed, retry the upload
    this.setError(undefined);
    this.uploadFile();
  };

  /** Cancels the current upload */
  public cancel = (): void => {
    if (this.isUploading()) {
      // An upload is progress, attempt to cancel it.
      // If the cancel succeeds (and the file is not uploaded), the activity will be deleted
      this.cancelRequested = true;
      //this.uploadPromise.abort();
    } else {
      // No upload is in progress - we are likely in an error state.
      // It's also possible that the upload has "just" finished and the progress animation
      // is still in the process of finishing. In either case, the user wants to abort
      // the upload operation, so simply delete the activity and associated attachment.
      this.delete();
    }
  };

  /** Deletes the attachment and associated activity */
  public delete = (): void => {
    const ticketId = this.ticketContextService.ticket.ticketId;
    if (!this.isNew()) {
      // Delete the activity from the server.
      this.$log.debug('Deleting activity', this.activity.activityId);

      // Call delete from the ticketActivitiesResource instead of on the activity controller (which calls activity.$delete),
      // since the activity may not be a real $resource (if it was created as a local attachment)
      // const ticketActivitiesResource = this.ticketService.getTicketActivitiesResource(this.ticketContextService.ticket.ticketId);
      this.ticketService.deleteActivity(this.activity.activityId, ticketId).subscribe(
        () => {
          this.$log.debug('Activity deleted');
          // Removes the activity from our parent's list and results in the views
          // being updated and our controller instance unloaded.
          this.activityCtrl.delete();
          // Broadcast the deletion to allow additional cleanup (such as closing file preview)
          this.globalEventBusService.broadcast(GlobalEvents.activityDeleted, this.activity);
        },
        (err: any) => {
          this.$log.debug('Failed to delete activity');
          this.$log.error(err);
          this.setError(this.$filter.transform('attachment_DeleteActivity_Error'), false);
        }
      );
    } else {
      this.activityCtrl.delete();
      // this.notifyWatchers(parseInt(ticketId), false);
    }
  };

  /** Determines whether this is a new activity or not */
  public isNew = (): boolean => {
    return this.activity.activityId == null;
  };

  /** Determines whether this is a local attachment (not to be uploaded to server yet) */
  public isLocal = (): boolean => {
    return this.activity?.$$isLocal ?? false;
  };

  /** Determines if a file is currently being uploaded */
  public isUploading = (): boolean => {
    return this.uploadPromise !== undefined;
  };

  public setError(err: string, canRetry: boolean = true) {
    this.error = err;
    this.canRetry = canRetry;

    if (this.error) {
      this.activityCtrl.setActivityState('error');
    }
    this.updateActivityState();
  }

  /** Clears the current error */
  public clearError = (): void => {
    this.setError(undefined);
    this.errorHandlerService.clearError();
    this.updateActivityState();
  };

  /** Whether the attachment note should be shown */
  public showNote = (): boolean => {
    // Don't show notes for new attachments (not uploaded yet)
    return !this.isNew() && !this.file;
  };

  /** Whether the user is able to edit the note */
  public canEditNote = (): boolean => {
    // Always allow new notes to be edited (because this is actually a 'create' - not an edit)
    if (!this._islicenceExpired && this.noteActivity && this.noteActivity.activityId === undefined) {
      return true;
    }

    return this.canEdit;
  };

  /** Uploads the selected file to the server */
  private uploadFile = (allowRetry: boolean = true) => {
    this.progress = 0;
    this.cancelRequested = false;
    // const ticketId = this.ticketContextService.ticket.ticketId;
    // const uploadCompleteCallback = this.activity.onUploadComplete;

    const uploadPromise = this.attachmentService.uploadAttachment(this.file, 'attachment');
    this.clearError();
    uploadPromise.subscribe(
      resultData => {
        const result = <IAttachmentModel>resultData;
        this.activity.fileId = result.attachmentId;

        // Upload complete: set progress to 100% and add a small delay before clearing
        // the client-side 'file' object. This delay allows time for the final progress
        // animation to complete and provides adequate visual feedback for very quick uploads.
        const file = this.file;
        this.progress = 100;
        setTimeout(() => {
          this.file = undefined;
          // Need to manually trigger a digest cycle to update bindings
          // this.$scope.$apply(() => {
          //     // Fire upload complete callback (if it is set)
          //     if (uploadCompleteCallback) {
          //         uploadCompleteCallback(UploadCompleteReason.Success);
          //     }
          // });
        }, this.uploadCompleteDelay);

        // save the activity
        if (this.isNew()) {
          this.createActivity(result, file, this.activity.details.displayName, this.activity.description);
        }
        // this.notifyWatchers(parseInt(ticketId), true)
      },
      error => {
        this.$log.debug('Upload failed for file: ' + this.file.name);
        if (error.status === 413 || error.status === 403) {
          this.setError(error.message, false);
        } else {
          const message = this.$filter.transform(
            'attachment_FileSizeExceeded_Error',
            this.file.name,
            this.fileSizeFormatter.transform(2000000000)
          );
          this.setError(message, false);
        }
      }
    );
    // this.uploadPromise = this.activityService.uploadAttachment(this.file, null, this.activity.displayName);
    // this.uploadPromise
    //   .subscribe(
    //     (result: IAttachmentModel) => {
    //       // Upload complete: set progress to 100% and add a small delay before clearing
    //       // the client-side 'file' object. This delay allows time for the final progress
    //       // animation to complete and provides adequate visual feedback for very quick uploads.
    //       this.progress = 100;
    //       setTimeout(() => {
    //         this.file = undefined;
    //         // Need to manually trigger a digest cycle to update bindings
    //         // this.$scope.$apply(() => {
    //         //     // Fire upload complete callback (if it is set)
    //         //     if (uploadCompleteCallback) {
    //         //         uploadCompleteCallback(UploadCompleteReason.Success);
    //         //     }
    //         // });
    //       }, this.uploadCompleteDelay);

    //       this.createActivity(result, this.file, this.activity.displayName);

    //       this.updateActivityState();
    //       if (this.activityCtrl.save) {
    //         this.activityCtrl.save(this.activity);
    //       }

    //       // Save note activity if required
    //       this.updateNoteActivity(ticketId);

    //       this.clearError();
    //     },
    //     undefined,
    //     // (evt: any) => {
    //     //   // Update the attachment activity progress
    //     //   this.progress = Math.min(100, (100 * evt.loaded) / evt.total);
    //     // }
    //   );
    //  .catch((err: any) => {
    //     let reason: UploadCompleteReason;
    //     if (this.cancelRequested) {
    //       // Upload cancelled - remove our activity
    //       reason = UploadCompleteReason.Cancelled;
    //       this.$log.debug('Upload cancelled');
    //       this.delete();
    //     } else {
    //       // Upload failed unexpectedly
    //       reason = UploadCompleteReason.Error;
    //       this.$log.debug('Upload failed for file: ' + this.file.name);
    //       if (err.status === 413 || err.status === 403) {
    //         this.setError(err.error_description, false);
    //       } else {
    //         const message = this.$filter.transform('attachment_Upload_Error', this.file.name, this.file.size);
    //         //this.$filter('saFileSizeFormat')(this.file.size));
    //         this.setError(message, allowRetry);
    //       }
    //     }
    //     err.isHandled = true;

    //     if (uploadCompleteCallback) {
    //       uploadCompleteCallback(reason);
    //     }
    //   })
    //   .finally(() => {
    //     // Upload is either complete or encountered an error - re-enable ticket for saving
    //     this.uploadPromise = undefined;
    //     if (this.activity.displayName) {
    //       this.setupCustomIconCallback();
    //     }
    //   });
  };

  private createActivity = (
    attachmentModel: IAttachmentModel,
    file: File,
    displayName: string,
    descriptions: string
  ) => {
    this.error = '';
    const temp = {
      activityId: this.activity.activityId,
      activityType: ActivityTypeEnum[this.activity.activityTypeId],
      fileName: attachmentModel.fileName,
      fileSize: attachmentModel.fileSize,
      fileId: attachmentModel.attachmentId,
      creationDate: new Date().toUTCString(),
      displayName: displayName,
      description: '',
      // thumbnail: string,
    } as IAttachmentActivity;

    if (this.isSPPMode) {
      temp.description = descriptions;
    }

    this.ticketService
      .saveActivity(temp, this.ticketContextService.ticket.ticketId)
      .pipe(
        map((a: IActivityModel) => {
          return {
            activityId: a.activityId,
            creationDate: a.creationDate,
            updateDate: a.updateDate,
          };
        })
      )
      .subscribe(
        data => {
          // const saveResult = <any>data;

          // It's crucial to maintain the reference, as this activity is propogated throughout the code
          Object.assign(this.activity, data);

          this.ticketService
            .getActivity(parseInt(this.ticketContextService.ticket.ticketId, 10), this.activity.activityId)
            .pipe(
              debounceTime(1000),
              map((a: any) => {
                // this is not really returning the IActivityModel but a flavour of Activity
                // TODO - model fixes
                const da = a as AttachmentActivity;
                if (typeof da.details === 'object' && Object.keys(da.details).includes('0')) {
                  da.details = da.details[0];
                }
                da.$$isLocal = false;
                return da;
              })
            )
            .subscribe(
              result => Object.assign(this.activity, result), // update activity with the $$isLocal
              err => console.error(err)
            );
          this.addedAttachment.emit();
        },
        err => {
          this.error = this.$filter.transform('attachment_Save_Error');
          console.error(err);
          this.updateActivityState();
        }
      );
  };

  private updateActivity = (attachmentModel: AttachmentActivity) => {
    this.error = '';
    const temp = Object.assign(
      {
        activityType: ActivityTypeEnum[attachmentModel.activityTypeId],
        fileName: attachmentModel.details.fileName,
        fileSize: attachmentModel.details.fileSize,
        displayName: attachmentModel.details.displayName,
      },
      attachmentModel
    );
    this.ticketService
      .saveActivity((<unknown>temp) as IAttachmentActivity, this.ticketContextService.ticket.ticketId)
      .subscribe(
        res => (this.activity.description = ((<unknown>res) as IAttachmentActivity).description),
        err => {
          console.error('updateActivity:', err);
          this.error = this.$filter.transform('note_Save_Error');
        }
      );
    this.updateActivityState();
  };

  public updateEditing(event) {
    this.editing = event;
    this.isEditting.emit(event);
    this.updateActivityState();
  }

  private updateActivityState() {
    let state: string;
    if (this.error) {
      state = 'error';
    } else {
      state = this.editing ? 'active' : 'inactive';
    }
    this.activityCtrl.setActivityState(state);
  }

  private setupCustomIconCallback = () => {
    this.activity.getCustomIconClass = () => {
      return this.attachmentService.isSupportedVideoType(this.activity.fileName)
        ? 'hd-icon hd-icon-recordVideo'
        : 'hd-icon hd-icon-screencap';
    };
  };

  private saveNote(event: AttachmentActivity) {
    this.updateActivity(event);
  }
}
